<template>
    <div class="container">
        <div class="row">
            <heading>{{ $t('core.masterData') }}
                <small> - {{ data.name }}</small>
            </heading>
        </div>
        <div class="row">
            <div class="card col-12">
                <div class="card-body">
                    <h2>{{ $t('core.masterData') }}</h2>
                    <div class="form-group" v-if="$root.isAdmin || $root.isSuperAdmin">
                        <label class="">{{ $t('company.fields.lgt_customer_number') }}
                            <required-icon></required-icon>
                        </label>
                        <input type="text" v-model="data.lgt_customer_number" class="form-control"/>
                        <small class="text-danger" v-if="errors['lgt_customer_number']">{{ errors['lgt_customer_number'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <label class="">{{ $t('company.fields.name') }}
                            <required-icon></required-icon>
                        </label>
                        <input type="text" v-model="data.name" class="form-control"/>
                        <small class="text-danger" v-if="errors['name']">{{ errors['name'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <address-search-input
                            v-model="data.address"
                            :label="$t('company.fields.address')"
                            @details="addressChanged(null, $event)"
                            :required="true"
                        ></address-search-input>
                        <small class="text-danger" v-if="errors['address']">{{ errors['address'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <label class="">{{ $t('company.fields.country') }}
                            <required-icon></required-icon>
                        </label>
                        <multiselect
                            v-model="data.country"
                            track-by="code"
                            label="name"
                            :options="countries"
                            :placeholder="$t('third_party.multiselect.placeholder')"
                            selectLabel=""
                            selectedLabel=""
                            deselectLabel=""
                        >
                            <template v-slot:noOptions>
                                {{ $t('third_party.multiselect.noOptions') }}
                            </template>
                        </multiselect>
                        <small class="text-danger" v-if="errors['country']">{{ errors['country'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <label class="">{{ $t('company.fields.postal_code') }}
                            <required-icon></required-icon>
                        </label>
                        <input type="text" v-model="data.postal_code" class="form-control"/>
                        <small class="text-danger" v-if="errors['postal_code']">{{ errors['postal_code'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <label class="">{{ $t('company.fields.city') }}
                            <required-icon></required-icon>
                        </label>
                        <input type="text" v-model="data.city" class="form-control"/>
                        <small class="text-danger" v-if="errors['city']">{{ errors['city'][0] }}</small>
                    </div>
                    <div class="form-group" v-if="$root.isAdmin || $root.isSuperAdmin">
                        <label>{{ $t('booking.deliveryForm') }}
                            <required-icon></required-icon>
                        </label>
                        <multiselect
                            v-model="selectedDeliveryForm"
                            track-by="id"
                            label="name"
                            :options="deliveryForms"
                            @input="deliveryFormSelected"
                            :placeholder="$t('third_party.multiselect.placeholder')"
                            selectLabel=""
                            selectedLabel=""
                            deselectLabel=""
                        >
                            <template v-slot:noOptions>
                                {{ $t('third_party.multiselect.noOptions') }}
                            </template>
                        </multiselect>
                        <small class="text-danger" v-if="errors['default_service_level']">{{ errors['default_service_level'][0] }}</small>
                    </div>

                    <div class="form-group">
                        <label class="">{{ $t('company.fields.label_email') }}</label>
                        <input type="text" v-model="data.label_email" class="form-control"/>
                        <small class="text-danger" v-if="errors['label_email']">{{ errors['label_email'][0] }}</small>
                    </div>

                    <template v-if="$root.isAdmin || $root.isSuperAdmin">
                        <hr class="mt-5"/>
                        <h2>{{ $t('company.incoterm') }}</h2>
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.incotermBooking') }}
                                <required-icon></required-icon>
                            </label>
                            <multiselect
                                :options="incoterms"
                                v-model="data.incoterm_booking"
                                track-by="key"
                                :searchable="false"
                                :allow-empty="false"
                                selectLabel=""
                                selectedLabel=""
                                deselectLabel=""
                                label="key"
                                :placeholder="$t('third_party.multiselect.placeholder')"
                            >
                                <template slot="option" slot-scope="{option}">
                                    {{ option.key }} <small class="text-muted">{{ option.name }}</small>
                                </template>
                            </multiselect>
                            <small class="text-danger" v-if="errors['incoterm_booking']">{{ errors['incoterm_booking'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="incoterm_booking" v-model="data.incoterm_booking_change">
                                <label class="custom-control-label" for="incoterm_booking">{{ $t('company.fields.incotermBookingChange') }}</label>
                            </div>
                        </div>
                        <template v-if="data.incoterm_booking_change">
                            <div class="form-group" v-for="incoterm in incoterms">
                                <div class="custom-control custom-checkbox">
                                    <input type="checkbox" class="custom-control-input" :id="'incoterm_booking-select' + incoterm.key" :value="incoterm.key"
                                           v-model="data.incoterm_booking_allowed">
                                    <label class="custom-control-label" :for="'incoterm_booking-select' + incoterm.key">{{ incoterm.key }}<small class="ml-2">{{
                                            incoterm.name
                                        }}</small></label>
                                </div>
                            </div>
                        </template>

                        <div class="form-group">
                            <label class="">{{ $t('company.fields.incotermReturn') }}
                                <required-icon></required-icon>
                            </label>
                            <multiselect
                                :options="incoterms"
                                v-model="data.incoterm_return"
                                track-by="key"
                                :searchable="false"
                                :allow-empty="false"
                                selectLabel=""
                                selectedLabel=""
                                deselectLabel=""
                                label="key"
                                :placeholder="$t('third_party.multiselect.placeholder')"
                            >
                                <template slot="option" slot-scope="{option}">
                                    {{ option.key }} <small class="text-muted">{{ option.name }}</small>
                                </template>
                            </multiselect>
                            <small class="text-danger" v-if="errors['incoterm_return']">{{ errors['incoterm_return'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="incoterm_return" v-model="data.incoterm_return_change">
                                <label class="custom-control-label" for="incoterm_return">{{ $t('company.fields.incotermReturnChange') }}</label>
                            </div>
                        </div>
                        <template v-if="data.incoterm_return_change">
                            <div class="form-group" v-for="incoterm in incoterms">
                                <div class="custom-control custom-checkbox">
                                    <input type="checkbox" class="custom-control-input" :id="'incoterm_return-select' + incoterm.key" :value="incoterm.key"
                                           v-model="data.incoterm_return_allowed">
                                    <label class="custom-control-label" :for="'incoterm_return-select' + incoterm.key">{{ incoterm.key }}<small class="ml-2">{{
                                            incoterm.name
                                        }}</small></label>
                                </div>
                            </div>
                        </template>

                        <hr class="mt-5"/>
                        <h2>{{ $t('company.deliveredBySender') }}</h2>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="delived_by_sender" v-model="data.delivered_by_sender">
                                <label class="custom-control-label" for="delived_by_sender">{{ $t('company.fields.deliveredBySender') }}</label>
                            </div>
                        </div>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="delived_by_sender_change" v-model="data.delivered_by_sender_change">
                                <label class="custom-control-label" for="delived_by_sender_change">{{ $t('company.fields.deliveredBySenderChange') }}</label>
                            </div>
                        </div>


                        <hr class="mt-5"/>
                        <h2>{{ $t('company.requiredValues') }}</h2>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="value_required_on_booking" v-model="data.value_required_on_booking">
                                <label class="custom-control-label" for="value_required_on_booking">{{ $t('company.fields.valueRequiredOnBooking') }}</label>
                            </div>
                        </div>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="loading_meters_required_on_booking" v-model="data.loading_meters_required_on_booking">
                                <label class="custom-control-label" for="loading_meters_required_on_booking">{{ $t('company.fields.loadingMetersRequiredOnBooking') }}</label>
                            </div>
                        </div>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="volume_required_on_booking" v-model="data.volume_required_on_booking">
                                <label class="custom-control-label" for="volume_required_on_booking">{{ $t('company.fields.volumeRequiredOnBooking') }}</label>
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="image_documentation_required_on_return_booking" v-model="data.image_documentation_required_on_return_booking">
                                <label class="custom-control-label" for="image_documentation_required_on_return_booking">{{ $t('company.fields.imageDocumentationRequiredOnReturnBooking') }}</label>
                            </div>
                        </div>

                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="send_to_ftp_on_pickup" v-model="data.send_to_ftp_on_pickup">
                                <label class="custom-control-label" for="send_to_ftp_on_pickup">{{ $t('company.fields.sendToFtpOnPickup') }}</label>
                            </div>
                        </div>
                    </template>

                    <template v-if="$root.isAdmin || $root.isSuperAdmin">
                        <hr class="mt-5"/>
                        <h2>{{ $t('booking.freightPayer') }}</h2>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="freight_payer" v-model="data.freightPayerActive">
                                <label class="custom-control-label" for="freight_payer">{{ $t('company.fields.alwaysFreightPayer') }}</label>
                            </div>
                        </div>
                        <template v-if="data.freightPayerActive">
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.name') }}
                                    <required-icon></required-icon>
                                </label>
                                <input type="text" v-model="data.freight_payer.name" class="form-control"/>
                                <small class="text-danger" v-if="errors['freight_payer.name']">{{ errors['freight_payer.name'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <address-search-input
                                    v-model="data.freight_payer.address"
                                    :label="$t('company.fields.address')"
                                    @details="addressChanged('freight_payer', $event)"
                                    :required="true"
                                ></address-search-input>
                                <small class="text-danger" v-if="errors['freight_payer.address']">{{ errors['freight_payer.address'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.country') }}
                                    <required-icon></required-icon>
                                </label>
                                <multiselect
                                    v-model="data.freight_payer.country"
                                    track-by="code"
                                    label="name"
                                    :options="countries"
                                    :placeholder="$t('third_party.multiselect.placeholder')"
                                    selectLabel=""
                                    selectedLabel=""
                                    deselectLabel=""
                                >
                                    <template v-slot:noOptions>
                                        {{ $t('third_party.multiselect.noOptions') }}
                                    </template>
                                </multiselect>
                                <small class="text-danger" v-if="errors['freight_payer.country']">{{ errors['freight_payer.country'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.postal_code') }}
                                    <required-icon></required-icon>
                                </label>
                                <input type="text" class="form-control" v-model="data.freight_payer.postal_code"/>
                                <small class="text-danger" v-if="errors['freight_payer.postal_code']">{{ errors['freight_payer.postal_code'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.city') }}
                                    <required-icon></required-icon>
                                </label>
                                <input type="text" v-model="data.freight_payer.city" class="form-control"/>
                                <small class="text-danger" v-if="errors['freight_payer.city']">{{ errors['freight_payer.city'][0] }}</small>
                            </div>
                        </template>
                        <div class="form-group">
                            <div class="custom-control custom-checkbox">
                                <input type="checkbox" class="custom-control-input" id="freight_payer_return" v-model="data.freightPayerReturnActive">
                                <label class="custom-control-label" for="freight_payer_return">{{ $t('company.fields.alwaysFreightPayerReturn') }}</label>
                            </div>
                        </div>
                        <template v-if="data.freightPayerReturnActive">
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.name') }}
                                    <required-icon></required-icon>
                                </label>
                                <input type="text" v-model="data.freight_payer_return.name" class="form-control"/>
                                <small class="text-danger" v-if="errors['freight_payer_return.name']">{{ errors['freight_payer_return.name'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <address-search-input
                                    v-model="data.freight_payer_return.address"
                                    :label="$t('company.fields.address')"
                                    @details="addressChanged('freight_payer_return', $event)"
                                    :required="true"
                                ></address-search-input>
                                <small class="text-danger" v-if="errors['freight_payer_return.address']">{{ errors['freight_payer_return.address'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.country') }}
                                    <required-icon></required-icon>
                                </label>
                                <multiselect
                                    v-model="data.freight_payer_return.country"
                                    track-by="code"
                                    label="name"
                                    :options="countries"
                                    :placeholder="$t('third_party.multiselect.placeholder')"
                                    selectLabel=""
                                    selectedLabel=""
                                    deselectLabel=""
                                >
                                    <template v-slot:noOptions>
                                        {{ $t('third_party.multiselect.noOptions') }}
                                    </template>
                                </multiselect>
                                <small class="text-danger" v-if="errors['freight_payer_return.country']">{{ errors['freight_payer_return.country'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.postal_code') }}
                                    <required-icon></required-icon>
                                </label>
                                <input type="text" class="form-control" v-model="data.freight_payer_return.postal_code"/>
                                <small class="text-danger" v-if="errors['freight_payer_return.postal_code']">{{ errors['freight_payer_return.postal_code'][0] }}</small>
                            </div>
                            <div class="form-group">
                                <label class="">{{ $t('company.fields.city') }}
                                    <required-icon></required-icon>
                                </label>
                                <input type="text" v-model="data.freight_payer_return.city" class="form-control"/>
                                <small class="text-danger" v-if="errors['freight_payer_return.city']">{{ errors['freight_payer_return.city'][0] }}</small>
                            </div>
                        </template>
                    </template>
                    <hr class="mt-5" />
                    <h2>{{ $t('company.fields.alternativeReturnAddress') }}</h2>
                    <div class="form-group">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="alternative_return_address" v-model="data.useAlternativeReturnAddressActive">
                            <label class="custom-control-label" for="alternative_return_address">{{ $t('company.fields.alternativeReturnAddress') }}</label>
                        </div>
                    </div>
                    <template v-if="data.useAlternativeReturnAddressActive">
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.name') }}<required-icon></required-icon></label>
                            <input type="text" v-model="data.alternative_return_address.name" class="form-control"/>
                            <small class="text-danger" v-if="errors['alternative_return_address.name']">{{ errors['alternative_return_address.name'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <address-search-input
                                v-model="data.alternative_return_address.address"
                                :label="$t('company.fields.address')"
                                @details="addressChanged('alternative_return_address', $event)"
                                :required="true"
                            ></address-search-input>
                            <small class="text-danger" v-if="errors['alternative_return_address.address']">{{ errors['alternative_return_address.address'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.country') }}<required-icon></required-icon></label>
                            <multiselect
                                v-model="data.alternative_return_address.country"
                                track-by="code"
                                label="name"
                                :options="countries"
                                :placeholder="$t('third_party.multiselect.placeholder')"
                                selectLabel=""
                                selectedLabel=""
                                deselectLabel=""
                            >
                                <template v-slot:noOptions>
                                    {{ $t('third_party.multiselect.noOptions') }}
                                </template>
                            </multiselect>
                            <small class="text-danger" v-if="errors['alternative_return_address.country']">{{ errors['alternative_return_address.country'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.postal_code') }}<required-icon></required-icon></label>
                            <input type="text" class="form-control" v-model="data.alternative_return_address.postal_code" />
                            <small class="text-danger" v-if="errors['alternative_return_address.postal_code']">{{ errors['alternative_return_address.postal_code'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.city') }}<required-icon></required-icon></label>
                            <input type="text" v-model="data.alternative_return_address.city" class="form-control"/>
                            <small class="text-danger" v-if="errors['alternative_return_address.city']">{{ errors['alternative_return_address.city'][0] }}</small>
                        </div>
                    </template>
                    <hr class="mt-5"/>
                    <h2>{{ $t('company.defaultPickup.heading') }}</h2>
                    <p class="text-muted small">{{ $t('company.defaultPickup.infoContext') }}</p>
                    <div class="form-group">
                        <label class="">{{ $t('pickup.contact_person_name') }}</label>
                        <input type="text" v-model="data.pickup_contact_name" class="form-control"/>
                        <small class="text-danger" v-if="errors['pickup_contact_name']">{{ errors['pickup_contact_name'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <label class="">{{ $t('pickup.contact_person_phone') }}</label>
                        <input type="text" v-model="data.pickup_contact_phone" class="form-control"/>
                        <small class="text-danger" v-if="errors['pickup_contact_phone']">{{ errors['pickup_contact_phone'][0] }}</small>
                    </div>
                    <div class="form-group">
                        <label class="">{{ $t('pickup.contact_person_email') }}</label>
                        <input type="text" v-model="data.pickup_contact_email" class="form-control"/>
                        <small class="text-danger" v-if="errors['pickup_contact_email']">{{ errors['pickup_contact_email'][0] }}</small>
                    </div>

                    <hr class="mt-5"/>
                    <h2>{{ $t('company.defaultPickupAddress.heading') }}</h2>
                    <p class="text-muted small">{{ $t('company.defaultPickupAddress.infoContext') }}</p>
                    <div class="form-group">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" id="default_pickup_address" v-model="data.useDefaultPickupAddressActive">
                            <label class="custom-control-label" for="default_pickup_address">{{ $t('company.defaultPickupAddress.heading') }}</label>
                        </div>
                    </div>
                    <template v-if="data.useDefaultPickupAddressActive">
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.name') }}<required-icon></required-icon></label>
                            <input type="text" v-model="data.default_pickup_address.name" class="form-control"/>
                            <small class="text-danger" v-if="errors['default_pickup_address.name']">{{ errors['default_pickup_address.name'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <address-search-input
                                v-model="data.default_pickup_address.address"
                                :label="$t('company.fields.address')"
                                @details="addressChanged('default_pickup_address', $event)"
                                :required="true"
                            ></address-search-input>
                            <small class="text-danger" v-if="errors['default_pickup_address.address']">{{ errors['default_pickup_address.address'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.country') }}<required-icon></required-icon></label>
                            <multiselect
                                v-model="data.default_pickup_address.country"
                                track-by="code"
                                label="name"
                                :options="countries"
                                :placeholder="$t('third_party.multiselect.placeholder')"
                                selectLabel=""
                                selectedLabel=""
                                deselectLabel=""
                            >
                                <template v-slot:noOptions>
                                    {{ $t('third_party.multiselect.noOptions') }}
                                </template>
                            </multiselect>
                            <small class="text-danger" v-if="errors['default_pickup_address.country']">{{ errors['default_pickup_address.country'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.postal_code') }}<required-icon></required-icon></label>
                            <input type="text" class="form-control" v-model="data.default_pickup_address.postal_code" />
                            <small class="text-danger" v-if="errors['default_pickup_address.postal_code']">{{ errors['default_pickup_address.postal_code'][0] }}</small>
                        </div>
                        <div class="form-group">
                            <label class="">{{ $t('company.fields.city') }}<required-icon></required-icon></label>
                            <input type="text" v-model="data.default_pickup_address.city" class="form-control"/>
                            <small class="text-danger" v-if="errors['default_pickup_address.city']">{{ errors['default_pickup_address.city'][0] }}</small>
                        </div>
                    </template>

                    <hr class="mt-5"/>
                    <h2>{{ $t('company.defaultGoodsDescription.heading') }}</h2>
                    <p class="text-muted small">{{ $t('company.defaultGoodsDescription.infoContext') }}</p>
                    <div class="form-group">
                        <label class="">{{ $t('company.defaultGoodsDescription.heading') }}</label>
                        <input type="text" v-model="data.default_goods_description" class="form-control"/>
                        <small class="text-danger" v-if="errors['default_goods_description']">{{ errors['default_goods_description'][0] }}</small>
                    </div>

                    <template v-if="$root.isAdmin || $root.isSuperAdmin">
                    <hr class="mt-5"/>
                    <h2>{{ $t('company.terminals.heading') }}</h2>
                    <p class="text-muted small">{{ $t('company.terminals.infoContext') }}</p>
                    <div class="form-group">
                        <label class="">{{ $t('company.fields.primaryTerminal') }}<required-icon></required-icon></label>
                        <multiselect
                            v-model="data.primary_terminal"
                            track-by="id"
                            label="name"
                            :options="terminals"
                            :placeholder="$t('third_party.multiselect.placeholder')"
                            selectLabel=""
                            selectedLabel=""
                            deselectLabel=""
                        >
                            <template v-slot:noOptions>
                                {{ $t('third_party.multiselect.noOptions') }}
                            </template>
                        </multiselect>

                        <small class="text-danger" v-if="errors['primary_terminal']">{{ errors['primary_terminal'][0] }}</small>
                    </div>
                    <div class="clearfix">
                        <button class="btn btn-primary float-right mb-4" @click="addTerminal">{{ $t('company.terminals.addCountrySpecificTerminal') }}</button>
                    </div>
                    <div class="row mb-4" v-if="data.terminals.length > 0">
                        <div class="col-6">
                            {{ $t('company.terminals.country') }}
                        </div>
                        <div class="col-5">
                            {{ $t('company.terminals.terminal') }}
                        </div>
                    </div>
                    <div v-for="(terminal,index) in data.terminals" class="row mb-2">
                        <div class="col-6">
                            <multiselect
                                v-model="data.terminals[index].country"
                                track-by="code"
                                label="name"
                                :options="availableTerminalCountries"
                                :placeholder="$t('third_party.multiselect.placeholder')"
                                selectLabel=""
                                selectedLabel=""
                                deselectLabel=""
                            >
                                <template v-slot:noOptions>
                                    {{ $t('third_party.multiselect.noOptions') }}
                                </template>
                            </multiselect>
                        </div>
                        <div class="col-5">
                            <multiselect
                                v-model="data.terminals[index].terminal"
                                track-by="id"
                                label="name"
                                :options="terminals"
                                :placeholder="$t('third_party.multiselect.placeholder')"
                                selectLabel=""
                                selectedLabel=""
                                deselectLabel=""
                            >
                                <template v-slot:noOptions>
                                    {{ $t('third_party.multiselect.noOptions') }}
                                </template>
                            </multiselect>
                        </div>
                        <div class="col-1">
                            <button class="btn btn-danger" @click="data.terminals.splice(index, 1)">{{ $t('core.actions.delete') }}</button>
                        </div>
                    </div>
                    </template>
                    <button class="btn btn-primary float-right" @click="store">{{ $t('core.actions.save') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AddressSearchInput from '@/Components/AddressSearchInput'
import clonedeep from 'lodash.clonedeep'

export default {
    name: 'CompanyEdit',
    components: { AddressSearchInput },
    data: () => {
        return {
            isAdmin: lgt.isAdmin,
            errors: [],
            countries: lgt.countries,
            deliveryForms: [],
            incoterms: lgt.incoterms,
            terminals: lgt.terminals,
            selectedDeliveryForm: null,
            data: { ...lgt.company, ...lgt.freightPayer, ...lgt.alternativeReturnAddress, ...lgt.defaultPickupAddress },
        }
    },
    watch: {
        'data.user.company.incoterm_booking_change': function () {
            this.data.incoterm_booking_allowed = []
        },
        'data.user.company.incoterm_return_change': function () {
            this.data.incoterm_return_allowed = []
        },
    },
    methods: {
        addressChanged (key, val) {
            if (key === null) {
                if(val.name !== ''){
                    this.data.name = val.name
                }
                this.data.address = val.address
                this.data.postal_code = val.zipCode
                this.data.city = val.city
                this.data.country = this.countries.find((x) => x.code === val.countryShort)
            } else {
                if(val.name !== ''){
                    this.data[key].name = val.name
                }
                this.data[key].address = val.address
                this.data[key].postal_code = val.zipCode
                this.data[key].city = val.city
                this.data[key].country = this.countries.find((x) => x.code === val.countryShort)
            }

        },
        deliveryFormSelected (val) {
            if (val === null) {
                this.data.default_service_level = null
            } else {
                this.data.default_service_level = val.id
            }
        },
        store () {
            let url = '/company/' + this.data.id
            let data = clonedeep(this.data);
            data.terminals = this.data.terminals.map((terminal) => {
                return {
                    terminal_id: terminal.terminal.id,
                    country_code: terminal.country.code
                }
            })
            data.primary_terminal = data.primary_terminal?.id;

            axios.post(url, data).then((response) => {
                this.$saveSuccess()
            }).catch((err) => {
                this.errors = err.response.data.errors
            })
        },
        addTerminal () {
            this.data.terminals.push({
                terminal: null,
                country: null
            })
        },
    },
    computed: {
        availableTerminalCountries: function () {
            return this.countries.filter((country) => {
                return !this.data.terminals.some((terminal) => terminal.country?.code === country.code)
            })
        },
    },
    created () {
        for (let [key, value] of Object.entries(lgt.deliveryForms)) {
            this.deliveryForms.push({
                id: key,
                name: key + ' ' + value,
            })
        }
        this.selectedDeliveryForm = this.deliveryForms.filter((x) => x.id === lgt.company.default_service_level)
    },
}
</script>
